import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';
import BranchGroupList from '../../Shared/BranchGroupList.vue';
import CollapsibleSummary from '../../Shared/CollapsibleSummary.vue';

/**
 * مراقبة الصرف للمرضى — لإدارة الصيدلة ومسؤول النظام (طلب الإدارة 2026-10-07).
 * اطلاع فقط: المستشفيات أولاً بعدد فواتيرها ومرضاها وكمياتها، ومنها فواتير مستشفى،
 * وكل فاتورة تُفتح على أصنافها. نفس نمط «السندات حسب المستشفى».
 */
export default {
    name: 'PatientDispensesMonitor',
    mixins: [HelperMixin],
    components: { PaginationHelper, BranchGroupList, CollapsibleSummary },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canMonitorPatientDispensesUi()) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية مراقبة الصرف للمرضى.');
            this.href('/Home');
            return;
        }
        const q = this.$route.query || {};
        const branchId = Number(q.branchId);
        if (branchId) {
            this.viewMode = 'hospital';
            this.selectedBranch = { id: branchId, name: q.branchName || '' };
        } else {
            this.viewMode = q.view === 'all' ? 'all' : 'hospitals';
        }
        this.refreshAll();
    },

    data() {
        return {
            // hospitals: قائمة المستشفيات · hospital: فواتير مستشفى · all: كل الفواتير
            viewMode: 'hospitals',
            selectedBranch: null,

            filters: { from: null, to: null, search: '', specializedOnly: false, status: 0 },

            branchRows: [],
            branchLoading: false,

            Info: [],
            totalCount: 0,
            pageNo: 1,
            pageSize: 25,
            loadingList: false,

            statistics: {},
            // الفواتير المفتوحة: { [id]: { loading, detail } }
            openRows: {},
        };
    },

    computed: {
        currentBranchId() {
            return this.viewMode === 'hospital' && this.selectedBranch ? this.selectedBranch.id : null;
        },
        summaryTitle() {
            if (this.viewMode === 'hospital' && this.selectedBranch) return 'ملخص — ' + (this.selectedBranch.name || 'المستشفى');
            return 'ملخص — كل المستشفيات';
        },
        summaryHint() {
            const s = this.statistics || {};
            const parts = [(s.invoices || 0) + ' فاتورة', (s.patients || 0) + ' مريض'];
            if (s.specializedInvoices) parts.push('تخصصية: ' + s.specializedInvoices);
            if (s.lastOn) parts.push('آخر صرف: ' + moment(s.lastOn).format('DD/MM/YYYY'));
            return parts.join(' · ');
        },
        statsCards() {
            const s = this.statistics || {};
            return [
                this.statCard('فواتير منفذة', s.invoices, 'primary', 'ti ti-receipt'),
                this.statCard('مرضى', s.patients, 'info', 'ti ti-users'),
                this.statCard('إجمالي الكميات', s.quantity, 'success', 'ti ti-pill', 'بالوحدة الأساسية'),
                this.statCard('فواتير بأدوية تخصصية', s.specializedInvoices, 'danger', 'ti ti-shield-check', 'من قائمة إدارة الصيدلة'),
            ];
        },
        topDrugs() {
            return (this.statistics && this.statistics.topDrugs) || [];
        },
        branchGroupRows() {
            return (this.branchRows || []).map((r) => ({
                key: r.branchesId,
                id: r.branchesId,
                name: r.branchName || '—',
                total: r.invoices || 0,
                lastDate: r.lastOn,
                badges: [
                    { label: 'تخصصية', value: r.specializedInvoices, cls: 'bg-label-danger' },
                    { label: 'مرضى', value: r.patients, cls: 'bg-label-info' },
                    { label: 'كمية', value: r.quantity, cls: 'bg-label-success' },
                    { label: 'ملغاة', value: r.cancelled, cls: 'bg-label-secondary' },
                ],
            }));
        },
    },

    methods: {
        apiFilters() {
            return {
                from: this.filters.from || null,
                to: this.filters.to || null,
                search: (this.filters.search || '').trim() || null,
                specializedOnly: !!this.filters.specializedOnly,
            };
        },

        refreshAll() {
            this.loadStatistics();
            if (this.viewMode === 'hospitals') this.loadBranchGroups();
            else this.GetInfo(1);
        },

        ApplyFilters() {
            this.refreshAll();
        },

        ResetFilters() {
            this.filters = { from: null, to: null, search: '', specializedOnly: false, status: 0 };
            this.refreshAll();
        },

        loadStatistics() {
            return this.$http.GetPatientDispenseMonitorStatistics({ ...this.apiFilters(), branchId: this.currentBranchId })
                .then((r) => { this.statistics = (r && r.data) || {}; })
                .catch(() => { this.statistics = {}; });
        },

        loadBranchGroups() {
            this.branchLoading = true;
            return this.$http.GetPatientDispenseMonitorByBranch(this.apiFilters())
                .then((r) => { this.branchRows = (r && r.data && r.data.info) || []; })
                .catch(() => { this.branchRows = []; })
                .finally(() => { this.branchLoading = false; });
        },

        GetInfo(pageNo) {
            this.pageNo = pageNo || 1;
            this.loadingList = true;
            return this.$http.GetPatientDispenseMonitorList({
                ...this.apiFilters(),
                branchId: this.currentBranchId,
                status: this.filters.status || 0,
                pageNo: this.pageNo,
                pageSize: this.pageSize,
            })
                .then((r) => {
                    this.Info = (r && r.data && r.data.info) || [];
                    this.totalCount = (r && r.data && r.data.count) || 0;
                })
                .catch(() => { this.Info = []; this.totalCount = 0; })
                .finally(() => { this.loadingList = false; });
        },

        openBranch(row) {
            this.selectedBranch = { id: row.id, name: row.name };
            this.switchView('hospital');
        },

        switchView(mode) {
            this.viewMode = mode;
            if (mode !== 'hospital') this.selectedBranch = null;
            this.openRows = {};
            const query = {};
            if (mode === 'hospital' && this.selectedBranch) {
                query.branchId = String(this.selectedBranch.id);
                query.branchName = this.selectedBranch.name;
            } else if (mode === 'all') {
                query.view = 'all';
            }
            this.$router.replace({ path: this.$route.path, query }).catch(() => { });
            this.refreshAll();
        },

        isRowOpen(item) {
            return !!(item && this.openRows[item.id]);
        },
        rowState(item) {
            return this.openRows[item.id] || {};
        },
        toggleRow(item) {
            if (this.openRows[item.id]) {
                this.$delete(this.openRows, item.id);
                return;
            }
            this.$set(this.openRows, item.id, { loading: true, detail: null });
            this.$http.GetPatientDispenseMonitorOne(item.id)
                .then((r) => {
                    if (this.openRows[item.id]) this.$set(this.openRows, item.id, { loading: false, detail: (r && r.data && r.data.info) || null });
                })
                .catch(() => {
                    if (this.openRows[item.id]) this.$set(this.openRows, item.id, { loading: false, detail: null });
                });
        },

        formatDate(d) {
            return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—';
        },
        formatNum(n) {
            return Number(n || 0).toLocaleString('en-US');
        },
    },
};
